// Sticky action bar that appears when one or more grid rows are
// selected. Phase 6 ships two actions backed by existing endpoints —
// CSV export and soft-delete (move to trash). Bulk PATCH and bulk
// merge are gated behind a server-side bulk endpoint we haven't built
// yet; when those land, add buttons here without changing callers.

import type { ReactNode } from 'react';

interface BulkActionBarProps {
  count: number;
  onClear(): void;
  busy?: boolean;
  children: ReactNode;
}

export function BulkActionBar({ count, onClear, busy, children }: BulkActionBarProps) {
  if (count === 0) return null;
  return (
    <div className="surface border border-divider rounded-lg px-3 py-2 flex items-center gap-2 text-sm sticky top-2 z-20">
      <span className="text-strong font-medium">
        {count} selected
      </span>
      <span className="text-faint">·</span>
      <div className="flex items-center gap-1 flex-wrap">{children}</div>
      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        className="ml-auto text-xs text-muted hover:text-strong disabled:opacity-50"
      >
        Clear
      </button>
    </div>
  );
}

export function BulkAction({
  onClick,
  disabled,
  children,
  variant = 'secondary'
}: {
  onClick(): void;
  disabled?: boolean;
  children: ReactNode;
  variant?: 'secondary' | 'danger';
}) {
  const cls =
    variant === 'danger'
      ? 'text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
      : 'text-mid hover:surface-hover';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}
